// Cookie Management Utility for GDPR Compliance
// This service controls all tracking based on user consent

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
}

export const COOKIE_CONSENT_KEY = 'cookieConsent';
export const COOKIE_PREFERENCES_KEY = 'cookiePreferences';

class CookieManager {
  private static instance: CookieManager;
  private preferences: CookiePreferences | null = null;
  private callbacks: Set<(preferences: CookiePreferences) => void> = new Set();

  private constructor() {
    if (typeof window !== 'undefined') {
      this.loadPreferences();
    }
  }

  static getInstance(): CookieManager {
    if (!CookieManager.instance) {
      CookieManager.instance = new CookieManager();
    }
    return CookieManager.instance;
  }

  // Load preferences from localStorage
  private loadPreferences(): void {
    try {
      const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
      const prefsStr = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      
      if (consent && prefsStr) {
        this.preferences = JSON.parse(prefsStr);
      }
    } catch (error) {
      console.error('Error loading cookie preferences:', error);
    }
  }

  // Get current preferences
  getPreferences(): CookiePreferences | null {
    return this.preferences;
  }

  // Check if user has given consent (made any choice)
  hasConsent(): boolean {
    return localStorage.getItem(COOKIE_CONSENT_KEY) === 'true';
  }

  // Save preferences and notify listeners
  setPreferences(preferences: CookiePreferences): void {
    this.preferences = preferences;
    
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
      localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
      
      // Notify all listeners about preference changes
      this.callbacks.forEach(callback => callback(preferences));
      
      // Apply preferences immediately
      this.applyPreferences(preferences);
    } catch (error) {
      console.error('Error saving cookie preferences:', error);
    }
  }

  // Subscribe to preference changes
  onPreferencesChange(callback: (preferences: CookiePreferences) => void): () => void {
    this.callbacks.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);
    };
  }

  // Apply current preferences to tracking services
  private applyPreferences(preferences: CookiePreferences): void {
    // Handle PostHog
    if (preferences.analytics) {
      this.enablePostHog();
    } else {
      this.disablePostHog();
    }

    // Handle Google Analytics (for future use)
    if (preferences.analytics) {
      this.enableGoogleAnalytics();
    } else {
      this.disableGoogleAnalytics();
    }

    // Clean up unwanted cookies
    this.cleanupCookies(preferences);
  }

  // PostHog Management
  private enablePostHog(): void {
    if (typeof window !== 'undefined' && window.posthog) {
      window.posthog.opt_in_capturing();
      window.posthog.set_config({
        persistence: 'localStorage+cookie'
      });
    }
  }

  private disablePostHog(): void {
    if (typeof window !== 'undefined' && window.posthog) {
      window.posthog.opt_out_capturing();
      window.posthog.set_config({
        persistence: 'memory'
      });
    }
  }

  // Google Analytics Management (for future use)
  private enableGoogleAnalytics(): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'denied' // We don't use ads currently
      });
    }
  }

  private disableGoogleAnalytics(): void {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied'
      });
    }
  }

  // Clean up tracking cookies when user rejects them
  private cleanupCookies(preferences: CookiePreferences): void {
    if (!preferences.analytics) {
      this.deleteCookiesByPrefix(['ph_', '_ga', '_gid', '_gat']);
    }
    
    if (!preferences.marketing) {
      this.deleteCookiesByPrefix(['_fbp', '_fbc', '_gcl_au']);
    }
    
    if (!preferences.personalization) {
      this.deleteCookiesByPrefix(['_hjid', '_hjIncludedInPageviewSample']);
    }
  }

  // Helper to delete cookies by prefix
  private deleteCookiesByPrefix(prefixes: string[]): void {
    if (typeof document === 'undefined') return;
    
    document.cookie.split(';').forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      
      if (prefixes.some(prefix => name.startsWith(prefix))) {
        // Delete cookie for current domain
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        
        // Delete cookie for parent domain
        const domain = window.location.hostname;
        const parentDomain = domain.startsWith('www.') ? domain.slice(4) : domain;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${parentDomain}`;
      }
    });
  }

  // Clear all stored preferences (for testing/reset)
  reset(): void {
    this.preferences = null;
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    localStorage.removeItem(COOKIE_PREFERENCES_KEY);
    
    // Clean up all tracking cookies
    this.deleteCookiesByPrefix(['ph_', '_ga', '_gid', '_gat', '_fbp', '_fbc', '_gcl_au', '_hjid']);
  }

  // Check if specific category is allowed
  isAllowed(category: keyof CookiePreferences): boolean {
    return this.preferences?.[category] ?? false;
  }

  // Initialize tracking based on stored preferences (call this on app load)
  initializeTracking(): void {
    if (this.preferences) {
      this.applyPreferences(this.preferences);
    }
  }
}

// Export singleton instance
export const cookieManager = CookieManager.getInstance();

// Extend Window interface for TypeScript
declare global {
  interface Window {
    posthog?: any;
    gtag?: (...args: any[]) => void;
  }
}